import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCheck } from "lucide-react";
import HudFrame from "@/components/primitives/HudFrame";
import { useNotifications } from "@/contexts/NotificationsContext";
import NotificationRow from "./NotificationRow";

const FILTERS = [
    { id: "all",    label: "All" },
    { id: "unread", label: "Unread" },
];

export default function NotificationsDrawer() {
    const { notifications, unread, drawerOpen, closeDrawer, markRead, markAllRead, remove, wsStatus } = useNotifications();
    const [filter, setFilter] = useState("all");

    const items = notifications.filter((n) => (filter === "unread" ? !n.read : true));

    return (
        <AnimatePresence>
            {drawerOpen && (
                <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    exit={{ opacity: 0 }}
                    transition={{ duration: 0.2 }}
                    onClick={closeDrawer}
                    className="fixed inset-0 z-[55] bg-tca-bg-deep/55 backdrop-blur-sm"
                    data-testid="notifications-drawer-backdrop"
                >
                    <motion.aside
                        initial={{ x: "100%" }}
                        animate={{ x: 0 }}
                        exit={{ x: "100%" }}
                        transition={{ type: "spring", stiffness: 250, damping: 30 }}
                        onClick={(e) => e.stopPropagation()}
                        data-testid="notifications-drawer"
                        className="absolute top-0 right-0 h-full w-full sm:w-[420px] bg-tca-bg-panel/95 backdrop-blur-xl flex flex-col"
                        style={{ boxShadow: "0 0 60px rgba(0,219,255,0.18)" }}
                    >
                        <HudFrame
                            bracketColor="var(--tca-cyan-core)"
                            bracketSize={14}
                            borderTint="rgba(0,219,255,0.22)"
                            className="h-full flex flex-col"
                            innerClassName="flex flex-col h-full"
                        >
                            {/* Header */}
                            <div className="px-5 py-4 border-b border-tca-cyan/15 flex items-center justify-between gap-3">
                                <div className="flex items-center gap-3">
                                    <span className="font-mono-tight text-[11px] uppercase tracking-[0.32em] text-tca-cyan/90">
                                        // signal feed
                                    </span>
                                    {unread > 0 && (
                                        <span className="font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-cyan-hi">
                                            {unread} new
                                        </span>
                                    )}
                                </div>
                                <button
                                    type="button"
                                    onClick={closeDrawer}
                                    aria-label="Close"
                                    data-testid="notifications-drawer-close"
                                    className="text-tca-ink-muted hover:text-tca-cyan p-1"
                                >
                                    <X size={16} />
                                </button>
                            </div>

                            {/* Filters */}
                            <div className="px-5 py-3 flex items-center justify-between border-b border-tca-cyan/10">
                                <div className="flex gap-1">
                                    {FILTERS.map((f) => (
                                        <button
                                            key={f.id}
                                            type="button"
                                            onClick={() => setFilter(f.id)}
                                            data-testid={`notif-filter-${f.id}`}
                                            className={`
                                                font-mono-tight text-[10px] uppercase tracking-[0.22em] px-3 py-1.5 rounded-full border
                                                transition-all
                                                ${filter === f.id
                                                    ? "border-tca-cyan text-tca-cyan-hi bg-tca-cyan/10"
                                                    : "border-tca-cyan/22 text-tca-ink-muted hover:text-tca-cyan hover:border-tca-cyan/55"}
                                            `}
                                        >
                                            {f.label}
                                        </button>
                                    ))}
                                </div>
                                {unread > 0 && (
                                    <button
                                        type="button"
                                        onClick={markAllRead}
                                        data-testid="notif-mark-all-read"
                                        className="inline-flex items-center gap-1 font-mono-tight text-[10px] uppercase tracking-[0.22em] text-tca-ink-muted hover:text-tca-cyan transition-colors"
                                    >
                                        <CheckCheck size={11} /> Mark all
                                    </button>
                                )}
                            </div>

                            {/* List */}
                            <div
                                className="flex-1 overflow-y-auto"
                                data-testid="notifications-drawer-list"
                            >
                                {items.length === 0 ? (
                                    <div className="px-5 py-16 text-center">
                                        <div className="font-mono-tight text-[10px] uppercase tracking-[0.32em] text-tca-cyan/65">
                                            // no signals
                                        </div>
                                        <p className="mt-3 text-[12px] text-tca-ink-muted leading-snug">
                                            Ecosystem listening. Notifications surface here as the system responds.
                                        </p>
                                    </div>
                                ) : (
                                    <AnimatePresence initial={false}>
                                        {items.map((n) => (
                                            <NotificationRow
                                                key={n.id}
                                                n={n}
                                                onRead={markRead}
                                                onRemove={remove}
                                            />
                                        ))}
                                    </AnimatePresence>
                                )}
                            </div>

                            {/* Footer */}
                            <div className="px-5 py-3 border-t border-tca-cyan/15 flex items-center justify-between font-mono-tight text-[9px] uppercase tracking-[0.24em]">
                                <span className="flex items-center gap-2 text-tca-ink-muted">
                                    <motion.span
                                        animate={{ opacity: wsStatus === "open" ? [0.55, 1, 0.55] : 0.5 }}
                                        transition={{ duration: 2.2, repeat: Infinity }}
                                        className={`inline-block w-1.5 h-1.5 rounded-full ${wsStatus === "open" ? "bg-tca-cyan shadow-[0_0_8px_rgba(0,219,255,0.7)]" : "bg-tca-ink-muted"}`}
                                    />
                                    ecosystem · {wsStatus === "open" ? "listening" : "reconnecting"}
                                </span>
                                <span className="text-tca-cyan/55">XI</span>
                            </div>
                        </HudFrame>
                    </motion.aside>
                </motion.div>
            )}
        </AnimatePresence>
    );
}
